// ChoiceDialog — the one-decision chooser (ADR-038 §1 — the Dialog row): a
// section with several verbs asks ONE question first ("What happened?" — the
// setup changed · correct the record · classify first), the verbs as tiles with
// a help line each, and the chosen verb's own surface (a drawer) follows. The
// Modal's card face with the question as its title; Cancel is its only footer
// act — a tile IS the decision, so there is no confirm button. `onChoose(id)`
// fires after `onClose`, so the caller opens the next surface on a clean
// overlay stack. The page's section link and the peek's Edit-menu row open the
// SAME dialog (`SectionAct` · `EditMenu` mount it from the registry row's
// `choices`); it is never composed by hand in an entity module. Rung-2
// composite over Modal + ChoiceTiles — props-only.

import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { ChoiceTiles } from '../ui/ChoiceTiles.js';
import type { ResolvedChoice } from './entityRegistry.js';

export interface ChoiceDialogProps {
  open: boolean;
  /** Cancel · Escape · the backdrop. */
  onClose: () => void;
  /** The question ("What happened?"). */
  title: string;
  /** The verbs — each a tile with its help line (`ResolvedChoice`, the registry row's `choices` resolved). */
  choices: readonly ResolvedChoice[];
  /** The chosen verb's id — fired after the dialog closed; the caller runs it. */
  onChoose: (id: string) => void;
}

export function ChoiceDialog({ open, onClose, title, choices, onChoose }: ChoiceDialogProps) {
  const { t } = useTranslation('common');
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('actions.cancel')}
        </Button>
      }
    >
      {/* One column of decision tiles — the arrows walk, a press chooses
          (`chooseOnPress`: a native radio's choice-per-arrow would decide at the
          first step). No tile is pre-chosen: the question is open until pressed. */}
      <ChoiceTiles
        ariaLabel={title}
        size="lg"
        gridClassName="grid-cols-1"
        chooseOnPress
        value={null}
        onChange={(id) => {
          onClose();
          onChoose(id);
        }}
        options={choices.map((c) => ({
          value: c.id,
          icon: c.icon,
          label: (
            <span className="flex min-w-0 flex-col gap-0.5">
              <span>{c.label}</span>
              <span className="text-xs font-normal text-[var(--color-text-secondary)]">{c.help}</span>
            </span>
          ),
        }))}
      />
    </Modal>
  );
}
